<template>
    <div>
        <!-- Filters Card -->
        <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            height="4"
        ></v-progress-linear>
        <v-card flat class="pa-3 mt-5">
            <v-row dense>
                <v-col cols="auto">
                    <v-menu
                        v-model="menuDateRange"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="240px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                :value="dateRangeText"
                                label="Date Range"
                                prepend-icon="mdi-calendar-range"
                                readonly
                                dense
                                outlined
                                hide-details
                                v-bind="attrs"
                                v-on="on"
                                clearable
                                style="min-width: 280px"
                                @click:clear="clearDateRange"
                            />
                        </template>
                        <v-date-picker
                            v-model="dateRange"
                            range
                            no-title
                            :max="new Date().toISOString().substr(0, 10)"
                            :allowed-dates="allowedDates"
                            @input="onRangeInput"
                        />
                    </v-menu>
                </v-col>
                <v-col cols="auto">
                    <v-btn
                        color="primary"
                        :disabled="loading"
                        @click="searchWorkOrders"
                    >
                        <v-icon left>mdi-magnify</v-icon> Search
                    </v-btn>
                </v-col>
                <v-spacer />
                <v-col cols="auto" class="mt-n2">
                    <v-select
                        prepend-icon="mdi-list-status"
                        v-model="selectedStatus"
                        :items="
                            Object.entries(woStatus).map(([text, value]) => ({
                                text,
                                value,
                            }))
                        "
                        item-text="text"
                        item-value="value"
                        label="Status"
                        :disabled="loading"
                        @change="calculateIndicators"
                        hide-details
                    ></v-select>
                </v-col>
            </v-row>
        </v-card>

        <div class="mt-n5 pa-3">
            <!-- Work Order Indicators Cards - Desktop view -->
            <div v-if="!isMobile" class="d-flex flex-row flex-wrap">
                <div
                    v-for="insight of insights"
                    :key="insight.title"
                    class="d-flex justify-in-line"
                >
                    <v-card class="pa-2 mr-3 mt-5" width="220px" height="60px">
                        <v-card-text
                            class="d-flex my-0 py-0 mx-0 px-0"
                            width="100%"
                        >
                            <v-chip
                                class="mx-1 pa-0 white--text justify-center align-center"
                                :color="insight.color"
                                label
                                :style="{ height: '42px', width: '42px' }"
                            >
                                <v-icon
                                    center
                                    class="pa-0 justify-center align-center"
                                >
                                    {{ insight.icon }}
                                </v-icon>
                            </v-chip>
                            <div class="mb-1 mt-0 pt-0">
                                <div class="d-flex mr-0 pr-0 text-capitalize">
                                    <span
                                        :style="{
                                            'font-size': '16px',
                                            'font-weight': 'bold',
                                        }"
                                        >{{ insight.title }}</span
                                    >
                                </div>
                                <p class="black--text" style="font-size: 16px;">
                                    {{
                                        formatNumber(indicators[insight.value])
                                    }}
                                </p>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>
            </div>

            <!--Work Order Indicators Cards - Mobile View -->
            <div v-if="isMobile" class="pa-1">
                <div
                    v-for="(insight, index) in insights"
                    :key="index"
                    class="mb-3"
                >
                    <span class="font-weight-bold"> {{ insight.title }}: </span>
                    <span class="ml-2">
                        {{ formatNumber(indicators[insight.value].toFixed(2)) }}
                    </span>
                </div>
            </div>

            <!-- Work Order Chart -->
            <v-row class="mt-10" no-gutters>
                <v-col cols="12">
                    <Highcharts style="height: 60vh;" :options="chartColumn" />
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { Chart } from 'highcharts-vue'
import moment from 'moment'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'WOStatistics',
    components: {
        Highcharts: Chart,
    },
    data: () => ({
        loading: false,
        startDate: null,
        endDate: null,
        dateRange: [],
        menuDateRange: false,
        insights: [
            {
                color: '#ffa414',
                icon: 'mdi-factory',
                iconColor: '#1A5276',
                value: 'indicatorEnteredWO',
                title: 'Entered WO',
            },
            {
                color: '#3498DB',
                icon: 'mdi-weight-kilogram',
                iconColor: '#2874A6',
                value: 'indicatorEnteredKilograms',
                title: 'Entered Kilograms',
            },
            {
                color: '#20844c',
                icon: 'mdi-format-list-numbered',
                iconColor: '#1A5276',
                value: 'indicatorPartNumbersQuantity',
                title: `Part Numbers`,
            },
            {
                color: '#3498DB',
                icon: 'mdi-screw-lag',
                iconColor: '#2874A6',
                value: 'indicatorPiecesQuantity',
                title: 'Pieces',
            },
        ],
        enteredWoByDate: {},
        enteredKgByDate: {},
        partNumbersByDate: {},
        piecesQuantityByDate: {},
        indicators: {
            indicatorEnteredWO: 0,
            indicatorEnteredKilograms: 0,
            indicatorPartNumbersQuantity: 0,
            indicatorPiecesQuantity: 0,
        },
        woStatus: {
            'All Status': 'allStatus',
            Approved: 'approved',
            Finished: 'finished',
            Closed: 'closed',
            Deleted: 'deleted',
        },
        selectedStatus: undefined,
        processes: [],
        processesData: {},
        chartColumn: {},
        workOrders: [],
        loadedStartDate: null,
        loadedEndDate: null,
    }),
    async created() {
        this.chartColumn = {
            chart: {
                type: 'spline',
            },
            title: {
                text: '',
            },
            xAxis: {
                categories: [],
            },
            yAxis: {
                title: {
                    text: '',
                },
            },
            series: [],
            tooltip: {
                headerFormat: '<b>{point.x}</b><br/>',
                pointFormat: '{series.name}: {point.y}',
            },
            credits: {
                enabled: false,
            },
            accessibility: { enabled: false },
        }
    },
    async mounted() {
        try {
            this.loading = true
            this.selectedStatus = Object.values(this.woStatus)[0]
            // set first day of the month
            this.startDate = moment()
                .startOf('month')
                .format('YYYY-MM-DD')
            // set current day
            this.endDate = moment(new Date()).format('YYYY-MM-DD')
            this.dateRange = [this.startDate, this.endDate]
            // get settings
            const {
                data: { settings },
            } = await API.getSettings()
            if (settings.length > 0) {
                // get processes
                this.processes = settings.find(setting => {
                    return setting.name.toLowerCase() === 'processes'
                })?.processes
                if (this.processes.length > 0) {
                    this.processes.forEach(process => {
                        this.processesData[process.id] = process
                    })
                }
            }
            await this.loadWorkOrders(this.startDate, this.endDate)
            await this.calculateIndicators()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        dateRangeText() {
            if (!this.dateRange || this.dateRange.length === 0) return ''
            return [...this.dateRange].sort().join(' ~ ')
        },
        isMobile() {
            return this.$vuetify.breakpoint.smAndDown
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        // Report Data Functions
        async searchWorkOrders() {
            try {
                this.loading = true
                if (this.startDate && this.endDate) {
                    const alreadyLoaded =
                        this.loadedStartDate &&
                        this.loadedEndDate &&
                        this.startDate >= this.loadedStartDate &&
                        this.endDate <= this.loadedEndDate
                    if (!alreadyLoaded) {
                        await this.loadWorkOrders(this.startDate, this.endDate)
                    }
                }
                await this.calculateIndicators()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async loadWorkOrders(startDate, endDate) {
            this.workOrders = []
            const startDateExtended = moment(startDate)
                .clone()
                .subtract(4, 'month')
                .format('YYYY-MM-DD')
            const endDateExtended = moment(endDate)
                .clone()
                .add(1, 'day')
                .format('YYYY-MM-DD')
            this.workOrders = await API.getLatestWorkOrders({
                startDate: startDateExtended,
                endDate: endDateExtended,
            })
            this.loadedStartDate = startDate
            this.loadedEndDate = endDate
            this.formatWorkOrders()
        },
        formatWorkOrders() {
            this.workOrders = this.workOrders.filter(wo => wo.scheduledOn)
            this.workOrders.forEach(wo => {
                wo.scheduledOn = wo.scheduledOn
                    ? this.formatDate(
                          wo.scheduledOn.seconds || wo.scheduledOn._seconds
                      )
                    : null
                wo.finishDate = wo.finishDate
                    ? this.formatDate(
                          wo.finishDate.seconds || wo.finishDate._seconds
                      )
                    : null
                wo.closedDate = wo.closedDate
                    ? this.formatDate(
                          wo.closedDate.seconds || wo.closedDate._seconds
                      )
                    : null
                wo.deletedDate = wo.deletedDate
                    ? this.formatDate(
                          wo.deletedDate.seconds || wo.deletedDate._seconds
                      )
                    : null
                wo.partNumbers = wo.partNumbers
                    ? Object.values(wo.partNumbers)
                    : []
            })
        },
        async calculateIndicators() {
            try {
                this.loading = true
                // clear variables
                this.clearVariables()
                // get days of the month
                const daysInRange = this.getDaysInRange(
                    this.startDate,
                    this.endDate
                )
                let initialWorkOrders = _.cloneDeep(this.workOrders)
                let workOrders = initialWorkOrders
                //filter work orders if the user selected an specific status
                if (this.selectedStatus == 'approved') {
                    workOrders = initialWorkOrders.filter(wo => {
                        let result = true
                        if (!wo.scheduledOn || wo.scheduledOn > this.endDate)
                            return false
                        if (wo.finishDate && wo.finishDate > wo.scheduledOn) {
                            result =
                                wo.finishDate >= this.endDate &&
                                wo.scheduledOn <= this.endDate
                        }
                        if (
                            wo.rejectedDate &&
                            wo.rejectedDate >= wo.scheduledOn
                        ) {
                            result =
                                wo.rejectedDate >= this.endDate &&
                                wo.scheduledOn <= this.endDate
                        }
                        return result
                    })
                } else if (this.selectedStatus == 'finished') {
                    workOrders = initialWorkOrders.filter(wo => {
                        let result = true
                        if (!wo.finishDate || wo.finishDate > this.endDate)
                            return false
                        if (wo.closedDate && wo.closedDate > wo.finishDate) {
                            result =
                                wo.closedDate >= this.endDate &&
                                wo.finishDate <= this.endDate
                        }
                        if (wo.scheduledOn && wo.scheduledOn >= wo.finishDate) {
                            result =
                                wo.scheduledOn >= this.endDate &&
                                wo.finishDate <= this.endDate
                        }
                        return result
                    })
                } else if (this.selectedStatus == 'closed') {
                    workOrders = initialWorkOrders.filter(wo => {
                        let result = true
                        if (!wo.closedDate || wo.closedDate > this.endDate)
                            return false
                        if (wo.finishDate && wo.finishDate > wo.closedDate) {
                            result =
                                wo.finishDate >= this.endDate &&
                                wo.closedDate <= this.endDate
                        }
                        return result
                    })
                } else if (this.selectedStatus == 'deleted') {
                    workOrders = initialWorkOrders.filter(wo => {
                        if (
                            !wo.deletedDate ||
                            this.formatDate(wo.deletedDate) > this.endDate
                        )
                            return false
                        return this.formatDate(wo.deletedDate) <= this.endDate
                    })
                }
                await this.extractIndicatorsData({ workOrders, daysInRange })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async extractIndicatorsData({ workOrders, daysInRange }) {
            let enteredInDate = {}
            let enteredKgInDate = {}
            let partNumbersInDate = {}
            let piecesQtyInDate = {}
            if (workOrders && workOrders.length > 0) {
                workOrders.forEach(wo => {
                    //entered validation
                    if (daysInRange.includes(wo.scheduledOn)) {
                        if (enteredInDate[wo.scheduledOn]) {
                            enteredInDate[wo.scheduledOn] += 1
                        } else {
                            enteredInDate[wo.scheduledOn] = 1
                        }

                        const woMass = this.calculateTotalKg(wo)
                        if (enteredKgInDate[wo.scheduledOn]) {
                            enteredKgInDate[wo.scheduledOn] += woMass
                        } else {
                            enteredKgInDate[wo.scheduledOn] = woMass
                        }

                        if (partNumbersInDate[wo.scheduledOn]) {
                            partNumbersInDate[wo.scheduledOn] +=
                                wo.partNumbers.length
                        } else {
                            partNumbersInDate[wo.scheduledOn] =
                                wo.partNumbers.length
                        }

                        let piecesQty =
                            wo.partNumbers && wo.partNumbers.length > 0
                                ? wo.partNumbers.reduce(
                                      (acc, partNumber) =>
                                          acc + (+partNumber.quantity || 0),
                                      0
                                  )
                                : 0
                        if (piecesQtyInDate[wo.scheduledOn]) {
                            piecesQtyInDate[wo.scheduledOn] += piecesQty
                        } else {
                            piecesQtyInDate[wo.scheduledOn] = piecesQty
                        }
                    }
                })
            }

            daysInRange.forEach(date => {
                if (!enteredInDate[date]) {
                    enteredInDate[date] = 0
                }
                if (!enteredKgInDate[date]) {
                    enteredKgInDate[date] = 0
                }
                if (!partNumbersInDate[date]) {
                    partNumbersInDate[date] = 0
                }
                if (!piecesQtyInDate[date]) {
                    piecesQtyInDate[date] = 0
                }
            })

            this.indicatorEnteredWO = Object.values(enteredInDate).reduce(
                (total, value) => total + value,
                0
            )
            this.indicatorEnteredKilograms = Object.values(
                enteredKgInDate
            ).reduce((total, value) => {
                return total + value
            }, 0)
            this.indicatorPartNumbersQuantity = Object.values(
                partNumbersInDate
            ).reduce((total, value) => {
                return total + value
            }, 0)
            this.indicatorPiecesQuantity = Object.values(
                piecesQtyInDate
            ).reduce((total, value) => total + value, 0)
            this.enteredWoByDate = enteredInDate
            this.enteredKgByDate = enteredKgInDate
            this.partNumbersByDate = partNumbersInDate
            this.piecesQuantityByDate = piecesQtyInDate
            this.indicators = {
                indicatorEnteredWO: this.indicatorEnteredWO,
                indicatorEnteredKilograms: this.indicatorEnteredKilograms,
                indicatorPartNumbersQuantity: this.indicatorPartNumbersQuantity,
                indicatorPiecesQuantity: this.indicatorPiecesQuantity,
            }
            await this.printData()
        },
        calculateTotalKg(wo) {
            let totalKg = 0
            if (wo.partNumbers && wo.partNumbers.length > 0) {
                wo.partNumbers.forEach(partNumber => {
                    totalKg +=
                        (+partNumber.mass || 0) * (+partNumber.quantity || 0)
                })
            }
            return totalKg
        },
        // Chart Functions
        async printData() {
            const sortDates = Object.keys(this.enteredKgByDate).sort()

            this.chartColumn.xAxis.categories = sortDates
            this.chartColumn.tooltip = {
                headerFormat: '<b>{point.x}</b><br/>',
                pointFormatter: function() {
                    const unit = this.series.name === 'Entered Kg' ? ' Kg' : ''
                    return `${this.series.name}: ${this.y}${unit}<br/>`
                },
            }
            this.chartColumn.series = [
                {
                    name: 'Entered WO',
                    data: sortDates.map(date => ({
                        name: date,
                        y: parseFloat(
                            this.enteredWoByDate[date]?.toFixed(2) || 0
                        ),
                    })),
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                    },
                },
                {
                    name: 'Entered Kg',
                    data: sortDates.map(date => ({
                        name: date,
                        y: parseFloat(
                            this.enteredKgByDate[date]?.toFixed(2) || 0
                        ),
                    })),
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                    },
                },
                {
                    name: 'Part Numbers',
                    data: sortDates.map(date => ({
                        name: date,
                        y: parseFloat(
                            this.partNumbersByDate[date]?.toFixed(2) || 0
                        ),
                    })),
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                    },
                },
                {
                    name: 'Pieces',
                    data: sortDates.map(date => ({
                        name: date,
                        y: parseFloat(
                            this.piecesQuantityByDate[date]?.toFixed(2) || 0
                        ),
                    })),
                    dataLabels: {
                        enabled: true,
                        format: '{y}',
                    },
                },
            ]
            this.menu = false
        },
        clearVariables() {
            this.enteredWoByDate = {}
            this.enteredKgByDate = {}
            this.partNumbersByDate = {}
            this.piecesQuantityByDate = {}
            this.indicatorEnteredWO = 0
            this.indicatorEnteredKilograms = 0
            this.indicatorPartNumbersQuantity = 0
            this.indicatorPiecesQuantity = 0
        },
        // Dates Functions
        formatDate(seconds) {
            return `${moment.unix(seconds).format('YYYY-MM-DD')}`
        },
        onRangeInput(value) {
            if (value.length === 2) {
                const [a, b] = [...value].sort()
                this.startDate = a
                this.endDate = b
                this.dateRange = [a, b]
                this.menuDateRange = false
            } else {
                this.startDate = value[0] || null
                this.endDate = null
            }
        },
        clearDateRange() {
            this.dateRange = []
            this.startDate = null
            this.endDate = null
        },
        allowedDates(date) {
            if (!this.dateRange || this.dateRange.length !== 1) return true
            const anchor = moment(this.dateRange[0])
            const d = moment(date)
            return d.isBetween(
                anchor.clone().subtract(2, 'months'),
                anchor.clone().add(2, 'months'),
                'day',
                '[]'
            )
        },
        getDaysInRange(start, end) {
            const days = []
            const current = moment.utc(start).startOf('day')
            const endDay = moment.utc(end).startOf('day')
            while (current.isSameOrBefore(endDay)) {
                days.push(current.format('YYYY-MM-DD'))
                current.add(1, 'day')
            }
            return days
        },
        // Numbers Functions
        formatNumber(value) {
            return new Intl.NumberFormat('de-DE', {
                maximumFractionDigits: 0,
            }).format(value)
        },
    },
}
</script>
